import * as CL from '@design-system/component-library';
import { type JSX, useEffect, useState } from 'react';
import { dsClass } from '../../common/constants/dsClasses.js';
import { activateSimCardMsg, returnMsg, simCardMsg, simCardTypeMsg, subChangePendingMsg, t } from '../../common/i18n';
import { SimType } from '../../generated/api/models.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { openDialog } from '../../public/site/dialogStore.js';
import { DetailsWrapper } from '../DetailsWrapper';
import { EmptyOrError } from '../EmptyOrError';
import { EsimActivation } from '../EsimActivation/EsimActivation.js';
import { Loading } from '../Loading';
import { PhysicalSimActivation } from '../PhysicalSimActivation/PhysicalSimActivation.js';
import type { CategoryKey } from '../../common/utils/categoryUtils.js';
import type { Address, CompanyInfoResponse, ServiceFeeResponse, Subscription } from '../../generated/api/models.js';
import type { AuthenticatedUser } from '../../public/site/authState';

import './SimActivation.scss';

export interface SimActivationProps {
  breadCrumbs: JSX.Element;
  companyInfo?: CompanyInfoResponse;
  category: CategoryKey;
  disallowBillableSimChanges: boolean;
  hasPendingActions: boolean;
  subscription: Subscription;
  getServiceFeeSim?: (
    simType: SimType,
    subscription: Subscription,
    address?: Address,
    user?: AuthenticatedUser | null
  ) => Promise<ServiceFeeResponse>;
  isEmployee?: boolean;
  onClickOrderSim: () => void;
  onClickSubscription: () => void;
}

export const SimActivationComponent = (props: SimActivationProps) => {
  const { companyInfo, getServiceFeeSim, subscription, isEmployee } = props;
  const [simChangeServiceFee, setSimChangeServiceFee] = useState<ServiceFeeResponse | undefined>(undefined);
  const [simType, setSimType] = useState(subscription?.details?.mobile?.simType || SimType.PHYSICAL);
  const { authenticatedUser } = useAuth();

  useEffect(() => {
    const fn = async () => {
      if (getServiceFeeSim && simChangeServiceFee === undefined && simType !== SimType.ESIM) {
        setSimChangeServiceFee(undefined);
        const serviceFeeSim = await getServiceFeeSim(simType, subscription, companyInfo?.address, authenticatedUser);
        setSimChangeServiceFee(serviceFeeSim);
      }
    };
    void fn();
  }, [authenticatedUser, companyInfo, getServiceFeeSim, simType, simChangeServiceFee, subscription]);

  if (!isEmployee && simChangeServiceFee === undefined && simType !== SimType.ESIM) {
    return <Loading />;
  }

  return (
    <>
      <h3 className={`ea-h3 ea-h3--thick ${dsClass.PADDING_BOTTOM_4}`}>{t.OLP0(simCardTypeMsg)}</h3>

      <CL.SelectGroup
        className={`of-sim-activation__sim-type ${dsClass.PADDING_0}`}
        onChange={selectedId => {
          if (selectedId) {
            setSimType(selectedId as SimType);
          }
        }}
        products={[
          {
            id: SimType.ESIM,
            name: 'eSIM',
            selected: simType === SimType.ESIM,
          },
          {
            id: SimType.PHYSICAL,
            name: t.PIZC(simCardMsg),
            selected: simType !== SimType.ESIM,
          },
        ]}
        value={simType}
      />

      {simType === SimType.ESIM ? (
        <EsimActivation {...props} />
      ) : (
        <PhysicalSimActivation
          {...props}
          onShowDialog={params => openDialog(params)}
          simChangeServiceFee={simChangeServiceFee?.price}
        />
      )}
    </>
  );
};

export const SimActivation = (props: SimActivationProps) => {
  const { breadCrumbs, hasPendingActions, onClickSubscription, subscription } = props;

  if (!subscription || !subscription.details || !subscription.details.mobile) {
    return <Loading />;
  }

  const { subscriptionContactName, subscriptionPurposeOfUse, subscriptionUserFriendlyId } = subscription;

  if (hasPendingActions) {
    return (
      <EmptyOrError
        id="pending-sub-changes"
        text={t.MQHO(subChangePendingMsg)}
        onButtonClick={onClickSubscription}
        buttonText={t.VH59(returnMsg)}
      />
    );
  }

  return (
    <DetailsWrapper
      detailsTop={breadCrumbs}
      id="of-activate-sim"
      heading={t.M1E7(activateSimCardMsg)}
      headingBottom={(subscriptionContactName || subscriptionPurposeOfUse) + ' | ' + subscriptionUserFriendlyId}
      heroPicto="sim-activate"
    >
      <SimActivationComponent {...props} />
    </DetailsWrapper>
  );
};
